import React, { Component } from 'react';

import AppTitle from 'AppTitle';
import Chip from 'Chip';
import ClassNames from 'classnames';
import DayPicker from './DayPicker';
import DurationPicker from './DurationPicker';
import Location from './Location';
import PropTypes from 'prop-types';
import { Translation, TranslationContext } from 'giro-react-toolkit/source/Translation';
import TimePicker from './TimePicker';

import dayIcon from "images/icon-day.svg";
import fromIcon from "images/icon-from.svg";
import timeIcon from "images/icon-time.svg";
import durIcon from "images/icon-dur.svg";
import menuIcon from "images/icon-menu.svg";

import './Scenarios.scss';

/**
 * Component that contains the form used to set a scenario
 */
class Scenarios extends Component {
   constructor(props) {
      super(props);
      this.state = {
         showOptions: true
      }

      this.location = React.createRef();
      this.dayPicker = React.createRef();
      this.timePicker = React.createRef();
      this.durationPicker = React.createRef();
      this.scenarioRef = React.createRef();

      this.disableFormSubmit = this.disableFormSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
   }

   componentWillMount() {
      document.addEventListener('touchstart', this.handleClick, false);
   }

   componentWillUnmount() {
      document.removeEventListener('touchstart', this.handleClick, false);
   }

   /**
    * Handles the click outside the scenario panel on SD to close the panel
    * @param event
    */
   handleClick(event) {
      if (!this.state.showOptions || !this.props.isInMap(event.target))
         return;

      this.hideOptions();
   }

   hideOptions() {
      this.setState(
         { showOptions: false }
      );
   }

   /**
    * Disable the form submission
    * @param event - The form event object
    */
   disableFormSubmit(event) {
      event.preventDefault();
   }

   /**
    * Handles the click on the location chip
    */
   editLocation() {
      this.setState(
         { showOptions: true },
         () => {
            this.location.current.focus();
         }
      );
   }

   /**
    * Handles the click on the day chip
    */
   editDay() {
      this.setState(
         { showOptions: true },
         () => {
            this.dayPicker.current.focus();
         }
      );
   }

   /**
    * Handles the click on the time chip
    */
   editTime() {
      this.setState(
         { showOptions: true },
         () => {
            this.timePicker.current.focus();
         }
      );
   }

   /**
    * Handles the click on the duration chip
    */
   editDur() {
      this.setState(
         { showOptions: true },
         () => {
            this.durationPicker.current.focus();
         }
      );
   }

   /**
    * Renders array of chips, chips are only available on small device
    * @param selectedLocationName
    * @param selectedDay
    * @param selectedTime
    * @param selectedDur
    */
   renderChips(selectedLocationName, selectedDay, selectedTime, selectedDur) {
      const chipsClasses = ClassNames(
         "scenario__chips",
         { "scenario__chips--hidden": this.state.showOptions }
      );

      const dayLabel = DayPicker.dayLabel(selectedDay);

      return (
         <div className={ chipsClasses }>
            <Chip icon={fromIcon} className="location-chip" onClick={() => {
               this.editLocation()
            }}>
               { selectedLocationName || <Translation resourceKey='ReachMapRes.NoLocationText' /> }
            </Chip>
            <Chip icon={dayIcon} onClick={() => {
               this.editDay()
            }}>{dayLabel}</Chip>
            <Chip icon={timeIcon} onClick={() => {
               this.editTime()
            }}>
               { selectedTime || <Translation resourceKey='ReachMapRes.NoTimeText' /> }
            </Chip>
            <Chip icon={durIcon} onClick={() => {
               this.editDur()
            }}>
               { selectedDur }
               &nbsp;
               <Translation resourceKey='ReachMapRes.DurationUnit' />
            </Chip>
         </div>);
   }

   /**
    * renders the Scenarios form component
    */
   render() {
      const optionClasses = ClassNames(
         "scenario__options",
         { "scenario__options--hidden": !this.state.showOptions }
      );

      return (
         <div ref={this.scenarioRef} className={ this.props.className }>
            { this.renderChips(this.props.selectedLocation.placeName, this.props.selectedDay, this.props.selectedTime, this.props.selectedDur) }
            <div id="option-panel" className={ optionClasses }>
               <header className="layout-header app-header">
                  <AppTitle />
                  <button className="header-menu" type="button" onClick={this.props.onMenuClick}
                     aria-label={this.context.localizeValue('ReachMapRes.MenuButtonText') }>
                     <img src={menuIcon} />
                  </button>
               </header>
               <form onSubmit={ this.disableFormSubmit }>
                  <Location
                     selectedLocation={ this.props.selectedLocation }
                     onSelectedLocation={ this.props.onSelectedLocation }
                     onClearLocation={ this.props.onClearLocation }
                     ref={ this.location } />
                  <DayPicker
                     selectedDay={ this.props.selectedDay }
                     onSelectedDay={ this.props.onSelectedDay }
                     ref={ this.dayPicker }/>
                  <TimePicker
                     selectedTime={ this.props.selectedTime }
                     onSelectedTime={ this.props.onSelectedTime }
                     ref={ this.timePicker }/>
                  <DurationPicker
                     className="scenario__duration"
                     selectedDur={ this.props.selectedDur }
                     onSelectedDur={ this.props.onSelectedDur }
                     ref={ this.durationPicker }/>
               </form>
               <button className="hide-options-button"
                  onClick={() => {
                     this.hideOptions()
                  }}
                  aria-label={this.context.localizeValue('ReachMapRes.HideButtonText')}>
               </button>
            </div>
         </div>
      );
   }
}

Scenarios.contextType = TranslationContext;

Scenarios.propTypes = {
   /**
    * defines the scenarios form css classnames
    */
   className: PropTypes.string,
   /**
    * Defines the selected location name
    */
   selectedLocation: PropTypes.object,
   /**
    * Defines the selected day value
    */
   selectedDay: PropTypes.string,
   /**
    * Defines the selected duration value
    */
   selectedDur: PropTypes.number,
   /**
    * Defines the selected time value
    */
   selectedTime: PropTypes.string,
   /**
    * A callback called when the location input cleared
    */
   onClearLocation: PropTypes.func,
   /**
    * A callback called when the layout changes
    */
   onLayoutChanged: PropTypes.func,
   /**
    * A callback called when a day is selected
    */
   onSelectedDay: PropTypes.func,
   /**
    * A callback called when a duration is selected
    */
   onSelectedDur: PropTypes.func,
   /**
    * A callback called when a location is selected
    */
   onSelectedLocation: PropTypes.func,
   /**
    * A callback called when a time is selected
    */
   onSelectedTime: PropTypes.func
}

export default Scenarios;
